import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    LinearProgress,
    Chip,
    Stack,
    Avatar,
    Divider,
    Button,
    Tooltip,
    Skeleton,
    IconButton,
    Collapse,
    useTheme,
} from '@mui/material';
import {
    EmojiEvents as TrophyIcon,
    Stars as CoinsIcon,
    TrendingUp as LevelIcon,
    WorkspacePremium as BadgeIcon,
    LocalFireDepartment as StreakIcon,
    Speed as XpIcon,
    ExpandMore as ExpandIcon,
    Leaderboard as LeaderboardIcon,
    Timeline as ProgressIcon,
} from '@mui/icons-material';
import { useLogto } from '@logto/react';
import { createGamificationService, GamificationService } from '../../services/GamificationService';

const AnimatedNumber = ({ value, duration = 1000, suffix = '' }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        let startTime;
        let animationFrame;

        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            
            setDisplayValue(Math.floor(value * progress));
            
            if (progress < 1) {
                animationFrame = requestAnimationFrame(animate);
            }
        };

        animationFrame = requestAnimationFrame(animate);
        
        return () => {
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
        };
    }, [value, duration]);

    return <span>{displayValue.toLocaleString()}{suffix}</span>;
};

const GamificationCard = ({ userId, accessToken, onViewLeaderboard, onViewAchievements }) => {
    const [gamificationData, setGamificationData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const { getAccessToken } = useLogto();
    const theme = useTheme();

    useEffect(() => {
        const loadGamificationData = async () => {
            try {
                setLoading(true);
                const gamificationService = createGamificationService(accessToken);
                
                const data = await gamificationService.getGamificationSummary(userId);
                setGamificationData(data);
                setError(null);
            } catch (err) {
                console.error('Failed to load gamification data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            loadGamificationData();
        }
    }, [userId, accessToken]);

    if (loading) {
        return (
            <Card elevation={3} sx={{ height: 'fit-content', mt: 6 }}>
                <CardContent>
                    <Skeleton variant="text" width="60%" height={32} />
                    <Skeleton variant="rectangular" width="100%" height={120} sx={{ mt: 2 }} />
                    <Skeleton variant="text" width="80%" height={24} sx={{ mt: 2 }} />
                    <Skeleton variant="text" width="70%" height={24} />
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card elevation={3} sx={{ height: 'fit-content', mt: 6 }}>
                <CardContent>
                    <Typography variant="h6" color="error">
                        Unable to load gamification data
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {error}
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    if (!gamificationData) {
        return null;
    }

    const { stats, recent_achievements, recentTransactions } = gamificationData;
    // Calculate achievements summary
    const unlocked_count = Array.isArray(recent_achievements)
        ? recent_achievements.filter(a => a.is_unlocked).length
        : 0;
    const total_count = Array.isArray(recent_achievements)
        ? recent_achievements.length
        : 0;
    const achievements = { unlocked_count, total_count };
    // Defensive fallback for stats fields
    const level = stats?.current_level ?? stats?.level ?? 1;
    const rank = stats?.rank_position ?? stats?.rank ?? '-';
    const coins = stats?.total_coins ?? stats?.coins ?? 0;
    const current_streak = stats?.streak_days ?? stats?.current_streak ?? 0;
    const best_streak = stats?.longest_streak ?? stats?.best_streak ?? 0;
    const total_xp = stats?.total_xp ?? 0;
    
    // Defensive fallback for level progress
    const levelProgress = GamificationService.getLevelProgress(
        Number(total_xp),
        Number(level)
    );
    
    const getRankColor = (rank) => {
        if (rank <= 3) return theme.palette.gamification.error;
        if (rank <= 10) return theme.palette.gamification.warning; 
        if (rank <= 50) return theme.palette.gamification.info;
        return theme.palette.text.secondary;
    };

    const getStreakEmoji = (streak) => {
        if (streak >= 30) return '🔥';
        if (streak >= 14) return '⚡';
        if (streak >= 7) return '💪';
        if (streak >= 3) return '👍';
        return '🌱';
    };

    return (
        <Card 
            elevation={0} 
            sx={{ 
                height: 'fit-content', 
                mt: 6,
                background: theme.palette.background.gamification,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '100%',
                    background: `radial-gradient(circle at top right, ${theme.palette.gamification.light}40, transparent 50%)`,
                    pointerEvents: 'none'
                }
            }}
        >
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    color: 'white',
                    fontWeight: 700,
                    mb: 2
                }}>
                    <ProgressIcon sx={{ mr: 1, color: theme.palette.gamification.gold }} />
                    Your Progress
                </Typography>

                {/* Level Section */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                            sx={{
                                background: `linear-gradient(135deg, ${theme.palette.gamification.gold}, ${theme.palette.gamification.warning})`,
                                width: 56,
                                height: 56,
                                mr: 2,
                                boxShadow: '0 4px 20px rgba(255,215,0,0.3)',
                            }}
                        >
                            <LevelIcon sx={{ fontSize: 28 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', lineHeight: 1 }}>
                                Level <AnimatedNumber value={Number(level) || 1} />
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                                <AnimatedNumber value={levelProgress.xpToNext || 0} /> XP to next level
                            </Typography>
                        </Box>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={levelProgress.percentage || 0}
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            bgcolor: 'rgba(255,255,255,0.2)',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 5,
                                background: `linear-gradient(90deg, ${theme.palette.gamification.gold}, ${theme.palette.gamification.warning})`,
                                boxShadow: '0 2px 10px rgba(255,215,0,0.4)',
                            },
                        }}
                    />
                    <Typography variant="caption" sx={{ 
                        mt: 1, 
                        display: 'block',
                        color: 'rgba(255,255,255,0.8)',
                        fontWeight: 500
                    }}>
                        {levelProgress.progressXp || 0} / {levelProgress.neededXp || 0} XP in this level
                    </Typography>
                </Box>

                {/* Quick Stats Grid */}
                <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                    <Tooltip title="Your current ranking">
                        <Chip
                            icon={<LeaderboardIcon />}
                            label={`#${rank}`}
                            sx={{ 
                                flexGrow: 1, 
                                bgcolor: theme.palette.gamification.secondary,
                                color: 'white', 
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                '&:hover': {
                                    bgcolor: theme.palette.gamification.secondary,
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Collected coins">
                        <Chip
                            icon={<CoinsIcon />}
                            label={<AnimatedNumber value={Number(coins) || 0} />}
                            sx={{ 
                                flexGrow: 1, 
                                bgcolor: theme.palette.gamification.coins, 
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                '&:hover': {
                                    bgcolor: theme.palette.gamification.coins,
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Learning streak">
                        <Chip
                            icon={<StreakIcon />}
                            label={`${current_streak} days`}
                            sx={{ 
                                flexGrow: 1, 
                                bgcolor: theme.palette.gamification.streak, 
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                '&:hover': {
                                    bgcolor: theme.palette.gamification.streak,
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        />
                    </Tooltip>
                </Stack>

                {/* Expandable Details */}
                <Box>
                    <Button
                        onClick={() => setExpanded(!expanded)}
                        endIcon={<ExpandIcon sx={{ 
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease'
                        }} />}
                        sx={{ 
                            color: 'white', 
                            mb: 1,
                            fontWeight: 600,
                            background: 'rgba(255,255,255,0.1)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: 2,
                            '&:hover': { 
                                background: 'rgba(255,255,255,0.2)',
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s ease'
                        }}
                        fullWidth
                    >
                        {expanded ? 'Show less' : 'Show details'}
                    </Button>

                    <Collapse in={expanded}>
                        <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.2)' }} />

                        {/* Detailed Stats */}
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <XpIcon sx={{ mr: 1, color: theme.palette.gamification.gold }} />
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Total XP</Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    <AnimatedNumber value={stats.total_xp} />
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <BadgeIcon sx={{ mr: 1, color: theme.palette.gamification.success }} />
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Achievements</Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {achievements.unlocked_count}/{achievements.total_count}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <StreakIcon sx={{ mr: 1, color: theme.palette.gamification.streak }} />
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Best Streak</Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {best_streak} days
                                </Typography>
                            </Box>
                        </Stack>

                        {/* Action Buttons */}
                        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                            <Button
                                variant="contained"
                                startIcon={<LeaderboardIcon />}
                                onClick={onViewLeaderboard}
                                sx={{ 
                                    flexGrow: 1,
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    '&:hover': { 
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                        transform: 'translateY(-1px)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                Leaderboard
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<BadgeIcon />}
                                onClick={onViewAchievements}
                                sx={{ 
                                    flexGrow: 1,
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    '&:hover': { 
                                        bgcolor: 'rgba(255,255,255,0.25)',
                                        transform: 'translateY(-1px)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                Achievements
                            </Button>
                        </Stack>

                        {/* Recent Activity */}
                        {recentTransactions && recentTransactions.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                    <ProgressIcon sx={{ mr: 1, fontSize: 18 }} />
                                    Recent Activity
                                </Typography>
                                <Box sx={{ 
                                    maxHeight: 120, 
                                    overflowY: 'auto',
                                    bgcolor: 'rgba(0,0,0,0.2)',
                                    borderRadius: 1,
                                    p: 1
                                }}>
                                    {recentTransactions.slice(0, 3).map((transaction, index) => (
                                        <Box key={index} sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            py: 0.5,
                                            borderBottom: index < 2 ? '1px solid rgba(255,255,255,0.1)' : 'none'
                                        }}>
                                            <Typography variant="caption">
                                                {transaction.description}
                                            </Typography>
                                            <Typography variant="caption" sx={{ 
                                                color: transaction.xp_change > 0 ? '#4CAF50' : '#F44336',
                                                fontWeight: 'bold'
                                            }}>
                                                {transaction.xp_change > 0 ? '+' : ''}{transaction.xp_change} XP
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Collapse>
                </Box>
            </CardContent>
        </Card>
    );
};

export { GamificationCard };
